/**
 * CustomizeBar + WidgetCanvas — hub layout customization.
 *
 * Model (see lib/widget-layout): a single ordered list of widgets, each
 * 'half' or 'full' width.
 *
 * View mode (WidgetCanvas):
 *   - walks the ordered list
 *   - 'full' widgets render as their own full-width row
 *   - consecutive 'half' widgets render in an auto-balancing block (CSS
 *     multi-column equalizes height — no manual fiddling, never lopsided)
 *
 * Edit mode:
 *   - a single reorderable list; every tile shows a toolbar:
 *     drag handle · nudge up/down · expand/collapse full-width · hide
 *   - drag a tile over an insertion bar to reorder
 *   - Reset / Done in the top bar; hidden widgets are chips you tap to re-add
 *
 * Native HTML5 DnD — no extra dep. Keyboard ↑↓ still works as a fallback.
 */
import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import type { UseWidgetLayout } from '@/lib/config/widget-layout';

interface BarProps {
  layout: UseWidgetLayout;
  widgetLabels: Record<string, string>;
  editMode: boolean;
  onToggleEdit: (next: boolean) => void;
}

export function CustomizeBar({ layout, widgetLabels, editMode, onToggleEdit }: BarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-1/60 px-3 py-2">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-2xs font-semibold uppercase tracking-wider text-ink-dim">
          {editMode ? 'Edit layout — drag to reorder, ⤢ to expand, or tap a chip to add' : 'Layout'}
        </span>
        {editMode && layout.hiddenIds.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {layout.hiddenIds.map((id) => (
              <button
                key={id}
                onClick={() => layout.toggle(id)}
                className="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-2xs font-medium text-ink-muted hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors"
                title={`Show ${widgetLabels[id] ?? id}`}
              >
                + {widgetLabels[id] ?? id}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {editMode && layout.isCustomized && (
          <button
            onClick={layout.reset}
            className="rounded-md px-2 py-1 text-2xs font-medium text-ink-dim hover:bg-surface-2 hover:text-ink transition-colors"
          >
            Reset to default
          </button>
        )}
        <button
          onClick={() => onToggleEdit(!editMode)}
          className={clsx(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-2xs font-medium transition-colors',
            editMode
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-surface-2 text-ink-muted hover:bg-surface-3 hover:text-ink',
          )}
        >
          <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
          {editMode ? 'Done' : 'Customize'}
        </button>
      </div>
    </div>
  );
}

// ── Drag state, module-level (only one drag at a time; dataTransfer isn't
// readable during dragover so we stash the id here). ───────────────────────
let DRAG_ID: string | null = null;

const GAP: Record<'sm' | 'md' | 'lg', string> = { sm: '0.75rem', md: '1rem', lg: '1.5rem' };

export interface WidgetCanvasProps {
  layout: UseWidgetLayout;
  widgetLabels: Record<string, string>;
  editMode: boolean;
  /** id → rendered node (return null to skip). */
  renderWidget: (id: string) => React.ReactNode | null;
  gap?: 'sm' | 'md' | 'lg';
}

/**
 * The widget area. View mode = interleaved full-width rows + auto-balanced
 * half-width blocks. Edit mode = a single reorderable list with per-tile
 * width toggle.
 */
export function WidgetCanvas({ layout, widgetLabels, editMode, renderWidget, gap = 'md' }: WidgetCanvasProps) {
  const ids = layout.orderedIds.filter(id => renderWidget(id) !== null);

  if (editMode) {
    return <EditList ids={ids} layout={layout} widgetLabels={widgetLabels} renderWidget={renderWidget} gap={gap} />;
  }

  // View mode — group consecutive same-width runs.
  const groups: Array<{ width: 'half' | 'full'; ids: string[] }> = [];
  for (const id of ids) {
    const w = layout.widthOf(id);
    const last = groups[groups.length - 1];
    if (w === 'full') {
      groups.push({ width: 'full', ids: [id] });
    } else if (last && last.width === 'half') {
      last.ids.push(id);
    } else {
      groups.push({ width: 'half', ids: [id] });
    }
  }

  return (
    <div className="flex flex-col" style={{ gap: GAP[gap] }}>
      {groups.map((g, gi) => {
        if (g.width === 'full') {
          return <div key={g.ids[0] ?? gi}>{renderWidget(g.ids[0]!)}</div>;
        }
        if (g.ids.length === 1) {
          // A lone half-width tile would float awkwardly; render it constrained
          // so it doesn't stretch the full row.
          return (
            <div key={g.ids[0]} className="lg:max-w-[calc(50%-0.5rem)]">
              {renderWidget(g.ids[0]!)}
            </div>
          );
        }
        // Auto-balancing block: CSS multi-column equalizes column heights.
        return (
          <div
            key={gi}
            className="lg:[column-count:2]"
            style={{ columnGap: GAP[gap] }}
          >
            {g.ids.map(id => (
              <div key={id} className="break-inside-avoid" style={{ marginBottom: GAP[gap] }}>
                {renderWidget(id)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function EditList({
  ids, layout, widgetLabels, renderWidget, gap,
}: {
  ids: string[];
  layout: UseWidgetLayout;
  widgetLabels: Record<string, string>;
  renderWidget: (id: string) => React.ReactNode | null;
  gap: 'sm' | 'md' | 'lg';
}) {
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const childRefs = useRef<Array<HTMLDivElement | null>>([]);

  function indexFromY(clientY: number): number {
    let idx = ids.length;
    for (let i = 0; i < ids.length; i++) {
      const el = childRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) { idx = i; break; }
    }
    return idx;
  }

  function applyDrop(clientY: number) {
    const dragged = DRAG_ID;
    DRAG_ID = null;
    setDropIdx(null);
    if (!dragged) return;
    const idx = indexFromY(clientY);
    const beforeId = idx >= ids.length ? null : (ids[idx] ?? null);
    layout.reorder(dragged, beforeId);
  }

  return (
    <div
      ref={listRef}
      className="relative flex flex-col rounded-lg border border-dashed border-border bg-surface-1/40 p-2"
      style={{ gap: GAP[gap] }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const next = indexFromY(e.clientY);
        if (next !== dropIdx) setDropIdx(next);
      }}
      onDragLeave={(e) => {
        if (!listRef.current?.contains(e.relatedTarget as Node | null)) setDropIdx(null);
      }}
      onDrop={(e) => { e.preventDefault(); applyDrop(e.clientY); }}
    >
      {ids.map((id, i) => (
        <div key={id} ref={(el) => { childRefs.current[i] = el; }} className="relative">
          {dropIdx === i && <InsertionBar />}
          <EditRow id={id} label={widgetLabels[id] ?? id} layout={layout}>
            {renderWidget(id)}
          </EditRow>
        </div>
      ))}
      {dropIdx === ids.length && ids.length > 0 && <InsertionBar />}
    </div>
  );
}

function InsertionBar() {
  return (
    <div
      className="h-1.5 -my-1 rounded-full bg-primary shadow-[0_0_12px_color-mix(in_srgb,var(--primary)_50%,transparent)] animate-pulse"
      aria-hidden="true"
    />
  );
}

function EditRow({
  id, label, layout, children,
}: {
  id: string;
  label: string;
  layout: UseWidgetLayout;
  children: React.ReactNode;
}) {
  const width = layout.widthOf(id);
  const isFull = width === 'full';
  return (
    <div
      draggable
      onDragStart={(e) => {
        DRAG_ID = id;
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', id); } catch { /* ignore */ }
      }}
      onDragEnd={() => { DRAG_ID = null; }}
      className={clsx(
        'relative rounded-xl ring-2 ring-primary/30 ring-offset-2 ring-offset-bg-1 cursor-grab active:cursor-grabbing select-none',
        !isFull && 'lg:max-w-[calc(50%-0.5rem)]',
      )}
      style={{ WebkitUserDrag: 'element' } as React.CSSProperties}
    >
      {/* Edit toolbar */}
      <div className="absolute -top-3 left-3 z-dropdown flex items-center gap-0.5 rounded-md border border-primary/40 bg-bg-1 px-1.5 py-0.5 shadow-lift">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-primary/60 mr-1">
          <circle cx="9" cy="6" r="1.5" /><circle cx="9" cy="12" r="1.5" /><circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="6" r="1.5" /><circle cx="15" cy="12" r="1.5" /><circle cx="15" cy="18" r="1.5" />
        </svg>
        <span className="px-1 text-2xs font-semibold text-primary">{label}</span>
        <span className="ml-1 rounded-sm bg-primary/10 px-1 text-[9px] font-mono uppercase text-primary/70">{isFull ? 'full' : 'half'}</span>
        <button
          onClick={(e) => { e.stopPropagation(); layout.toggleWidth(id); }}
          className="p-0.5 text-ink-dim hover:text-primary"
          title={isFull ? 'Collapse to half width' : 'Expand to full width'}
        >
          {isFull ? (
            <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 9H5V5M15 9h4V5M9 15H5v4M15 15h4v4" /></svg>
          ) : (
            <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
          )}
        </button>
        <button onClick={(e) => { e.stopPropagation(); layout.move(id, 'up'); }} className="p-0.5 text-ink-dim hover:text-primary" title="Nudge up">
          <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
        </button>
        <button onClick={(e) => { e.stopPropagation(); layout.move(id, 'down'); }} className="p-0.5 text-ink-dim hover:text-primary" title="Nudge down">
          <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
        </button>
        <button onClick={(e) => { e.stopPropagation(); layout.toggle(id); }} className="p-0.5 text-ink-dim hover:text-danger" title="Hide">
          <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div className="pointer-events-none">{children}</div>
      <div className="absolute inset-0 z-sticky cursor-grab active:cursor-grabbing" aria-hidden="true" />
    </div>
  );
}
